<template>
  <a-table
    v-if="!loading"
    :rowKey="(record) => record.FbFormId || 'page-row-' + record.PageName"
    :columns="facebookColumns"
    :dataSource="data"
    :pagination="false"
    :loading="loading"
    :rowSelection="rowSelection"
    :class="{ 'safari-fix': isSafari }"
  >
    <template slot="fb-formName" slot-scope="formName, record">
      <router-link v-if="record.FbFormId != null" :to="'/edit-form/' + record.FbPageId + '/' + record.FbFormId">
        {{ formName }}
        <span class="form-id">{{ record.FbFormId }}</span>
      </router-link>
      <h3 v-else>{{ record.PageName }}</h3>
    </template>

    <template slot="fb-last-request" slot-scope="lastRequest, record" v-if="record.FbFormId != null">
      <span v-if="lastRequest == '1970-01-01T00:00:00.000Z'">None</span>
      <router-link
        :to="{ path: `/view-logs/${record.FbFormId}`}"
        v-else
        :form-name="record.FormName"
        @click.native="changeFormName(record.FormName)"
      >
        {{ formatDate(lastRequest) }}
        <a-icon type="bars" />
      </router-link>
    </template>

    <template slot="fb-production-status" slot-scope="isProduction, record" v-if="record.FbFormId != null">
      <a-switch :defaultChecked="isProduction" @change="isProductionChanged(record, $event)"></a-switch>
    </template>

    <template slot="fb-mapping-status" slot-scope="status, record" v-if="record.FbFormId != null">
      <a-switch :defaultChecked="status == 'active'" @change="checkboxChanged(record, $event)"></a-switch>
    </template>
  </a-table>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

const facebookColumns = [
  {
    title: "Form",
    dataIndex: "FormName",
    // sorter: sortFields,
    scopedSlots: { customRender: "fb-formName" },
  },
  {
    title: "Last Request",
    dataIndex: "LastRequest",
    className: "centered-col",
    scopedSlots: { customRender: "fb-last-request" },
  },
  {
    title: "Production",
    dataIndex: "IsProduction",
    className: "centered-col",
    scopedSlots: { customRender: "fb-production-status" },
  },
  {
    title: "Active",
    dataIndex: "MappingStatus",
    className: "centered-col",
    scopedSlots: { customRender: "fb-mapping-status" },
  },
  {
    title: "Created By",
    dataIndex: "CreatedByEmail",
  },
];

export default {
  data() {
    return {
      facebookColumns,
      data: [],
    };
  },
  computed: {
    ...mapState(["user", "apiUrl"]),
    rowSelection() {
      return {
        onChange: (selectedRowKeys, selectedRows) => {
          this.setSelectedRows(selectedRows)
        },
        getCheckboxProps: (record) => ({
          props: {
            disabled: record.FbFormId == null, // Column configuration not to be checked
            class: "tableSelectCheckboxOverride", // not working!!!
          },
        }),
      };
    },
    // this little hack is to deal with position sticky elements not working
    // consistently across browsers that support it (I'm looking at you safari)
    isSafari() {
      return navigator.vendor && navigator.vendor.toLowerCase().indexOf("apple computer") > -1;
    },
  },
  methods: {
    // this returns back a function that is called when the checkbox changes.
    // this ensures the correct record is known
    checkboxChanged(rec, val) {
      axios({
        url: `${this.apiUrl}/mappings`,
        method: "post",
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          action: "SET_MAPPING_STATUS",
          pageId: rec.FbPageId,
          formId: rec.FbFormId,
          formStatus: val ? "active" : "inactive",
        },
      });
    },
    isProductionChanged(rec, val) {
      axios({
        url: `${this.apiUrl}/mappings`,
        method: "post",
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          action: "SET_PRODUCTION_STATUS",
          pageId: rec.FbPageId,
          formId: rec.FbFormId,
          prodStatus: val,
        },
      });
    },
    formatDate(d) {
      if (d == "1970-01-01T00:00:00.000Z") {
        return "None";
      }
      const date = new Date(d);
      return date.toLocaleString();
    },
    changeFormName(newName) {
      this.$store.commit("setFormName", newName);
    },
    async deleteForms() {
      const deleteMsg =
        this.selectedRows.length > 1
          ? "Are you sure you want to delete these mappings?"
          : "Are you sure you want to delete this mapping?";
      if (confirm(deleteMsg)) {
        this.setLoading(true);

        for (const row of this.selectedRows) {
          await axios({
            method: "post",
            url: `${this.apiUrl}/mappings`,
            headers: {
              Authorization: this.user.signInUserSession.idToken.jwtToken,
            },
            params: {
              action: "DELETE_MAPPING",
              pageId: row.FbPageId,
              formId: row.FbFormId,
            },
          }).then((rslt) => {
            this.loadForms();
            this.setSelectedRows([]);
          });
        }
      }
    },
    loadForms() {
      this.setLoading(true);
      this.data = [];
      axios({
        method: "post",
        url: `${this.apiUrl}/mappings`,
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          action: "GET_MAPPINGS",
        },
      })
        .then((rslt) => {
          const pageNames = [...new Set(rslt.data.map((row) => row.PageName))];
          pageNames.forEach((page) => this.data.push({ PageName: page, FormName: "0" }));
          this.data.push(...rslt.data);
          this.data = this.data.sort((a, b) => {
            if (a.PageName < b.PageName) {
              return -1;
            }
            if (a.PageName > b.PageName) {
              return 1;
            }
            if (a.FormName < b.FormName) {
              return -1;
            }
            if (a.FormName > b.FormName) {
              return 1;
            }
            return 0;
          });
          this.setLoading(false);
        })
        .catch((err) => {
          alert("Unable to load forms");
          console.log(err);
          this.setLoading(false);
        });
    },
  },
  mounted() {
    this.loadForms();
  },
  props: ["selectedRows", "setSelectedRows", "loading", "setLoading"],
};
</script>

<style lang="scss" scoped>
::v-deep {
  table .ant-table-thead {
    position: relative;
    z-index: 2;
    > tr > th {
      background: #fff;
      position: sticky;
      top: 0;
      z-index: 2;

      &.centered-col {
        text-align: center;
      }
    }
  }
  .ant-table-tbody > tr > td.centered-col {
    text-align: center;
  }
  tr[data-row-key^="page-row-"] {
    > td {
      position: sticky;
      top: 54px;
      z-index: 2;
      // NOTE: this is an exceptional case that !important is used.
      // Only allowed because overriding ant's styling which is unfortunately
      // nested deep so this helps keep it much more readable/maintainable
      // on our end.
      background: #fafafa !important;
    }
    .ant-table-selection-column span {
      visibility: hidden;
    }
    h3 {
      margin-bottom: 0;
      // stupid trick to get the title over so it visually is different than the
      // titles of the form titles.
      position: relative;
      left: -53px;
    }
    &:hover > td {
      // NOTE: this is an exceptional case that !important is used.
      // Only allowed because overriding ant's styling which is unfortunately
      // nested deep so this helps keep it much more readable/maintainable
      // on our end.
      background: #fafafa !important;
    }
  }
  .safari-fix {
    tr[data-row-key^="page-row-"] {
      > td {
        top: 0;
      }
    }
  }
}
</style>
